import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  computed,
  inject,
  Injectable,
  makeStateKey,
  PLATFORM_ID,
  signal,
  TransferState,
} from '@angular/core';
import { catchError, map, type Observable, of, tap } from 'rxjs';

import { BrowserApiService } from './browser-api.service';

export interface KudosItem {
  title: string;
  subtitle: string;
  url: string;
  icon?: string;
  avatar?: string;
  initials?: string;
}

interface NodeFs {
  existsSync(path: string): boolean;
  readFileSync(path: string, options: string): string;
}

interface NodePath {
  join(...paths: string[]): string;
}

declare const require: {
  (module: 'node:fs'): NodeFs;
  (module: 'node:path'): NodePath;
  (module: string): unknown;
};

declare const process: {
  cwd(): string;
};

@Injectable({
  providedIn: 'root',
})
export class KudosService {
  // =============================================================================
  // Public Properties
  // =============================================================================

  /**
   * Read-only signal of the current kudos items.
   */
  readonly kudosItems = computed(() => this._kudosItems());

  /**
   * Computed signal indicating if there are any kudos items available.
   */
  readonly hasKudos = computed(() => this._kudosItems().length > 0);

  /**
   * Read-only signal indicating if the kudos are currently being loaded.
   */
  readonly loading = computed(() => this._loading());

  // =============================================================================
  // Private Properties
  // =============================================================================

  private readonly http = inject(HttpClient);
  private readonly browserApi = inject(BrowserApiService);
  private readonly transferState = inject(TransferState);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly kudosPath = 'assets/data/kudos.json';
  private readonly KUDOS_DATA_KEY = makeStateKey<KudosItem[]>('kudos_data');

  private readonly _kudosItems = signal<KudosItem[]>([]);
  private readonly _loading = signal<boolean>(false);

  // =============================================================================
  // Constructor
  // =============================================================================

  constructor() {
    this.initializeKudos();
  }

  // =============================================================================
  // Public Methods
  // =============================================================================

  /**
   * Force a reload of the kudos data.
   */
  refreshKudos(): Observable<KudosItem[]> {
    this._loading.set(true);
    if (isPlatformServer(this.platformId)) {
      const items = this.getKudosServer();
      this._kudosItems.set(items);
      this._loading.set(false);
      return of(items);
    }

    return this.http.get<KudosItem[]>(this.kudosPath).pipe(
      map((items) => this.processKudosItems(items)),
      tap((items) => {
        this._kudosItems.set(items);
        this.transferState.set(this.KUDOS_DATA_KEY, items);
        this._loading.set(false);
      }),
      catchError(() => {
        this._kudosItems.set([]);
        this._loading.set(false);
        return of([]);
      }),
    );
  }

  /**
   * Legacy method to get kudos as an observable.
   * Now uses the signal as the source if already loaded.
   */
  getKudos(): Observable<KudosItem[]> {
    const current = this._kudosItems();
    if (current.length > 0) {
      return of(current);
    }

    return this.refreshKudos();
  }

  // =============================================================================
  // Private Methods
  // =============================================================================

  private initializeKudos(): void {
    const savedData = this.transferState.get(this.KUDOS_DATA_KEY, null);

    if (savedData && savedData.length > 0) {
      this._kudosItems.set(savedData);
    }

    if (isPlatformServer(this.platformId)) {
      const items = this.getKudosServer();
      this._kudosItems.set(items);
      this.transferState.set(this.KUDOS_DATA_KEY, items);
    } else if (!savedData || savedData.length === 0) {
      this.refreshKudos().subscribe();
    }
  }

  private processKudosItems(items: unknown): KudosItem[] {
    if (!Array.isArray(items)) return [];

    return items.map((item: KudosItem) => ({
      ...item,
      avatar: item.avatar || this.getAvatar(item.url),
      initials: this.getAcronym(item.title),
    }));
  }

  private getServerEnv(): { fs: NodeFs; path: NodePath; cwd: string } | null {
    const req = typeof require !== 'undefined' ? require : undefined;
    if (typeof req !== 'function') return null;

    const fs = req('node:fs');
    const path = req('node:path');
    if (!fs || !path) return null;

    const proc = typeof process !== 'undefined' ? process : undefined;
    if (!proc || typeof proc.cwd !== 'function') return null;

    return { fs, path, cwd: proc.cwd() };
  }

  private getKudosServer(): KudosItem[] {
    try {
      const env = this.getServerEnv();
      if (!env) return [];

      const { fs, path, cwd } = env;

      const paths = [
        path.join(cwd, 'src', this.kudosPath),
        path.join(cwd, 'public', this.kudosPath),
        path.join(cwd, 'dist/darena/browser', this.kudosPath),
      ];

      for (const p of paths) {
        if (fs.existsSync(p)) {
          const content = fs.readFileSync(p, 'utf8');
          const parsed: unknown = JSON.parse(content);
          return this.processKudosItems(parsed);
        }
      }
    } catch (e) {
      console.error('Error reading kudos on server:', e);
    }
    return [];
  }

  private getAcronym(title: string): string {
    return title
      .split(' ')
      .filter((n) => !!n)
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }

  private getAvatar(url: string): string | undefined {
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.replace('www.', '');
      const pathSegments = urlObj.pathname.split('/').filter((s) => s);

      if (pathSegments.length === 0) return undefined;

      let username = pathSegments[0];

      if (hostname === 'github.com') {
        return `https://github.com/${username}.png`;
      } else if (hostname === 'gitlab.com') {
        return `https://gitlab.com/${username}.png`;
      } else if (hostname === 'instagram.com') {
        if (['p', 'reels', 'stories', 'explore'].includes(username) && pathSegments.length > 1) {
          username = pathSegments[1];
        }
        return `https://unavatar.io/instagram/${username}?fallback=false`;
      } else if (hostname === 'facebook.com') {
        return `https://unavatar.io/facebook/${username}?fallback=false`;
      }
    } catch {
      return undefined;
    }
    return undefined;
  }
}
